import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';

/**
 * List all users (admin only)
 * @param {number} limit - Number of users to fetch
 * @param {number} offset - Offset for pagination
 * @returns {Promise<{success: boolean, users?: Array, total?: number, error?: string}>}
 */
export const listAllUsers = async (limit = 100, offset = 0) => {
  try {
    const listUsers = httpsCallable(functions, 'listAllUsers');
    const result = await listUsers({ limit, offset });

    return {
      success: true,
      users: result.data.users,
      total: result.data.total,
      limit: result.data.limit,
      offset: result.data.offset
    };
  } catch (error) {
    console.error('Error listing users:', error);
    return {
      success: false,
      error: error.message || 'Failed to list users'
    };
  }
};

/**
 * Delete a user (admin only)
 * @param {string} userId - User ID to delete
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
export const deleteUser = async (userId) => {
  try {
    const deleteUserFunc = httpsCallable(functions, 'deleteUser');
    const result = await deleteUserFunc({ userId });

    return {
      success: true,
      message: result.data.message,
      deletedTrips: result.data.deletedTrips,
      deletedNotifications: result.data.deletedNotifications
    };
  } catch (error) {
    console.error('Error deleting user:', error);
    return {
      success: false,
      error: error.message || 'Failed to delete user'
    };
  }
};

/**
 * Set admin status for a user (admin only)
 * @param {string} userId - User ID to update
 * @param {boolean} isAdmin - Whether user should be admin
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
export const setAdminStatus = async (userId, isAdmin) => {
  try {
    const setAdmin = httpsCallable(functions, 'setAdminStatus');
    const result = await setAdmin({ userId, isAdmin });

    return {
      success: true,
      message: result.data.message
    };
  } catch (error) {
    console.error('Error setting admin status:', error);
    return {
      success: false,
      error: error.message || 'Failed to set admin status'
    };
  }
};

/**
 * Check if current user is an admin
 * @param {Object} userData - User data from Firestore
 * @returns {boolean}
 */
export const isAdmin = (userData) => {
  return userData?.isAdmin === true;
};

/**
 * Get rate limit status for all users (admin only)
 * Shows which users have hit their daily limits
 * @returns {Promise<{success: boolean, rateLimits?: Array, date?: string, limits?: Object, error?: string}>}
 */
export const getRateLimitStatus = async () => {
  try {
    const getRateLimits = httpsCallable(functions, 'getRateLimitStatus');
    const result = await getRateLimits();

    return {
      success: true,
      rateLimits: result.data.rateLimits,
      date: result.data.date,
      limits: result.data.limits
    };
  } catch (error) {
    console.error('Error getting rate limit status:', error);
    return {
      success: false,
      error: error.message || 'Failed to get rate limit status'
    };
  }
};

/**
 * Update rate limit configuration (admin only)
 * @param {string} functionName - The function to update limit for
 * @param {number} newLimit - The new daily limit
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
export const updateRateLimitConfig = async (functionName, newLimit) => {
  try {
    const updateConfig = httpsCallable(functions, 'updateRateLimitConfig');
    const result = await updateConfig({ functionName, newLimit });

    return {
      success: true,
      message: result.data.message
    };
  } catch (error) {
    console.error('Error updating rate limit config:', error);
    return {
      success: false,
      error: error.message || 'Failed to update rate limit config'
    };
  }
};
